import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import {
  Avatar,
  Container,
  ContentArea,
  CustomSpan,
  DisplayArea,
  DropArea,
  DropItem,
  EmptySearchText,
  Filter,
  FiltersArea,
  Group,
  Label,
  LabelGroup,
  NetworkBadge,
  RightLabel,
  SearchText,
  SmallAvatar,
  SubdropArea,
  Thumbnail,
} from './index.styles';
import { IconNames, BrandColors, SemanticColors } from '../../../theme';
import { Icon } from '../../atoms/Icon';
import { Button } from '../Button';
import { Input } from '../Input';
import { Text } from '../../atoms/Text';
import { Footer, InputArea } from '../AdvancedSelect/index.styles';
import { Checkbox } from '../Checkbox';
import { Skeleton } from '../../atoms/Skeleton';

export interface SubfilterType {
  id: string;
  label: string;
  icon?: IconNames;
  iconColor?: BrandColors | SemanticColors;
  imageUrl?: string;
  subLabel?: string;
  showAsAvatar?: boolean;
  rightLabel?: string;
  isDisabled?: boolean;
  avatarColor?: string;
  avatarLabel?: string;
}

export interface AdvancedFilterSelectedOptions {
  id: string;
  label: string;
  selected: string[];
}

interface FilterType {
  id: string;
  label: string;
  subFilters: SubfilterType[];
}

type FilterOrSubfilter = FilterType | SubfilterType;
type SelectedByCategory = Record<string, string[]>;

export interface AdvancedFilterProps extends Component {
  /**
   * Clear all selected filters button label. Required.
   */
  clearAllLabel: string;
  /**
   * The filter button display label. Required.
   */
  displayLabel: string;
  /**
   * Text for empty filter state. Optional.
   */
  emptyFilterText?: string;
  /**
   * Array of filters to be displayed in the advanced filter. Each filter can have multiple sub-filters. Required.
   */
  filters: FilterOrSubfilter[];
  /**
   * Text for filter results state. Optional.
   */
  filterResultsText?: string;
  /**
   * Text for input label. Optional.
   */
  inputLabel?: string;
  /**
   * Icon name. If it has icon, icon will be applied. Optional. (You can see the full list at branding/icons)
   */
  icon?: IconNames;
  /**
   * Disable state. Optional.
   */
  isDisabled?: boolean;
  /**
   * Select option event emitter. Required.
   */
  onSelect: (selected: AdvancedFilterSelectedOptions[]) => void;
  /**
   * Footer select all button label. Required.
   */
  selectedAllLabel: string;
  /**
   * List of filters already selected. Optional.
   */
  selectedFilters: AdvancedFilterSelectedOptions[];
  /**
   * Footer unselect all button label. Required.
   */
  unselectedAllLabel: string;
  /**
   * List of filter IDs that are locked (non-removable, non-interactive as chips). Optional.
   */
  lockedFilterIds?: string[];
}

export const AdvancedFilter = ({
  clearAllLabel,
  displayLabel,
  emptyFilterText = 'No filters found matching',
  filters,
  filterResultsText = 'Contains',
  icon,
  inputLabel = '',
  isDisabled,
  lockedFilterIds = [],
  onSelect,
  selectedAllLabel,
  selectedFilters,
  testId = 'advanced-filter',
  unselectedAllLabel,
}: AdvancedFilterProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeSubDrop, setActiveSubDrop] = useState<number>();
  const [activeItemPosY, setActiveItemPosY] = useState(0);
  const [mainFilter, setMainFilter] = useState<string>('');
  const [textFilterValue, setTextFilterValue] = useState<string>('');
  const [subSearch, setSubSearch] = useState<string>('');
  const [subDropXPos, setSubDropXPos] = useState(0);
  const [failedImageIds, setFailedImageIds] = useState<Record<string, true>>({});
  const [openFilterCardId, setOpenFilterCardId] = useState<string | null>(null);
  const [subdropY, setSubdropY] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const subdropRef = useRef<HTMLDivElement | null>(null);
  const cardDropdownRef = useRef<HTMLDivElement | null>(null);
  const isSubfilterType = (filter: FilterOrSubfilter): filter is SubfilterType => {
    return !('subFilters' in filter);
  };
  // Fix 1: Handle viewport visibility directly via callback parameter
  const { ref: inViewRef } = useInView({
    threshold: 1,
    rootMargin: '8px',
    onChange: (inView) => {
      if (isCollapsed && !inView) {
        setIsCollapsed(false);
        setMainFilter('');
        setSubSearch('');
        setTextFilterValue('');
        setActiveSubDrop(undefined);
      }
    },
  });

  const { x, y, strategy, refs } = useFloating({
    open: isCollapsed,
    onOpenChange: setIsCollapsed,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const {
    x: cardX,
    y: cardY,
    strategy: cardStrategy,
    refs: cardRefs,
  } = useFloating({
    open: !!openFilterCardId,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  // Fix 2: Shield tracking hooks from direct property invocation checks inside JSX template compiler
  const handleReferenceRef = (node: HTMLElement | null) => {
    refs.setReference(node);
  };

  const handleFloatingRef = (node: HTMLDivElement | null) => {
    dropdownRef.current = node;
    refs.setFloating(node);
  };

  const handleCardFloatingRef = (node: HTMLDivElement | null) => {
    cardDropdownRef.current = node;
    cardRefs.setFloating(node);
  };

  // Fix 3: Calculate filtered IDs directly during the render pass via useMemo
  const filteredIdsByGroup = useMemo<Record<string, string[]>>(() => {
    if (activeSubDrop === undefined) return {};

    const group = filters[activeSubDrop];

    if (isSubfilterType(group)) {
      return { [group.id]: [group.id] };
    }

    return {
      [group.id]: group.subFilters.filter((s) => s.label.toLowerCase().includes(subSearch.toLowerCase())).map((s) => s.id),
    };
  }, [activeSubDrop, subSearch, filters]);

  const filteredSubfilters = useMemo(() => {
    if (activeSubDrop === undefined) return [];

    const group = filters[activeSubDrop];

    if (isSubfilterType(group)) {
      return group.label.toLowerCase().includes(subSearch.toLowerCase()) ? [group] : [];
    }

    return group.subFilters.filter((s) => s.label.toLowerCase().includes(subSearch.toLowerCase()));
  }, [activeSubDrop, subSearch, filters]);

  // Fix 4: Safely calculate coordinate dimensions inside an layout effect cycle
  useLayoutEffect(() => {
    const innerHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const activeListHeight = subdropRef.current?.offsetHeight ?? 0;

    if (activeItemPosY + activeListHeight >= innerHeight) {
      setSubdropY(innerHeight - activeListHeight);
    } else {
      setSubdropY(activeItemPosY);
    }
  }, [activeItemPosY, activeSubDrop]);

  const selectedFiltersByCategory: SelectedByCategory = selectedFilters.reduce((acc, g) => {
    acc[g.id] = g.selected;

    return acc;
  }, {} as SelectedByCategory);
  const selectedTextFilterValue = selectedFilters.find((filter) => filter.id === 'text')?.selected[0] || '';

  const markImageFailed = (id: string) => {
    setFailedImageIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const getSelectableIdsForGroup = (group: FilterType, availableIds: string[]): string[] => {
    if (availableIds.length === 0) return [];

    return group.subFilters.filter((s) => availableIds.includes(s.id) && !s.isDisabled).map((s) => s.id);
  };

  const isFooterSelectAllDisabled = useMemo(() => {
    if (activeSubDrop === undefined) return true;

    const group = filters[activeSubDrop];

    if (isSubfilterType(group)) return true;

    const availableFilteredIds = filteredIdsByGroup[group.id] ?? filteredSubfilters.map((s) => s.id);
    const selectableIds = getSelectableIdsForGroup(group, availableFilteredIds);

    return selectableIds.length === 0;
  }, [activeSubDrop, filters, filteredIdsByGroup, filteredSubfilters]);

  const allActiveSelected = (() => {
    if (activeSubDrop === undefined) return false;

    const group = filters[activeSubDrop];

    if (isSubfilterType(group)) {
      if (group.isDisabled) return false;

      const existing = selectedFilters.find((g) => g.id === group.id);

      return !!existing && existing.selected.includes(group.id);
    }

    const availableFilteredIds = filteredIdsByGroup[group.id] ?? filteredSubfilters.map((s) => s.id);
    const selectableIds = getSelectableIdsForGroup(group, availableFilteredIds);

    if (selectableIds.length === 0) return false;

    const existing = selectedFilters.find((g) => g.id === group.id);

    if (!existing) return false;

    return selectableIds.every((id) => existing.selected.includes(id));
  })();

  const handleSelect = (groupId: string, groupLabel: string, subId: string) => {
    const existing = selectedFilters.find((g) => g.id === groupId);
    let updated: AdvancedFilterSelectedOptions[];

    if (!existing) {
      updated = [...selectedFilters, { id: groupId, label: groupLabel, selected: [subId] }];
    } else {
      const already = existing.selected.includes(subId);
      const updatedGroup: AdvancedFilterSelectedOptions = {
        id: existing.id,
        label: existing.label,
        selected: already ? existing.selected.filter((v) => v !== subId) : [...existing.selected, subId],
      };

      if (updatedGroup.selected.length === 0) {
        updated = selectedFilters.filter((g) => g.id !== groupId);
      } else {
        updated = selectedFilters.map((g) => (g.id === groupId ? updatedGroup : g));
      }
    }

    onSelect(updated);
  };

  const handleSelectSearchText = (value = mainFilter) => {
    const trimmedMainFilter = value.trim();

    if (!trimmedMainFilter) return;

    const textFilter: AdvancedFilterSelectedOptions = {
      id: 'text',
      label: filterResultsText,
      selected: [trimmedMainFilter],
    };

    const updated = [...selectedFilters.filter((group) => group.id !== textFilter.id), textFilter];

    onSelect(updated);
  };

  const handleSelectAllActiveGroup = () => {
    if (activeSubDrop === undefined) return;

    const group = filters[activeSubDrop];

    if (isSubfilterType(group)) {
      if (group.isDisabled) return;

      handleSelect(group.id, group.label, group.id);

      return;
    }

    const availableIds = filteredIdsByGroup[group.id] ?? group.subFilters.map((s) => s.id);
    const selectableIds = getSelectableIdsForGroup(group, availableIds);

    if (selectableIds.length === 0) return;

    const existing = selectedFilters.find((g) => g.id === group.id);

    let updated: AdvancedFilterSelectedOptions[];

    if (!existing) {
      updated = [...selectedFilters, { id: group.id, label: group.label, selected: selectableIds }];
    } else {
      const allAlready = selectableIds.every((id) => existing.selected.includes(id));
      const newSelected = allAlready
        ? existing.selected.filter((id) => !selectableIds.includes(id))
        : Array.from(new Set([...existing.selected, ...selectableIds]));

      if (newSelected.length === 0) {
        updated = selectedFilters.filter((g) => g.id !== group.id);
      } else {
        updated = selectedFilters.map((g) => (g.id === group.id ? { id: g.id, label: g.label, selected: newSelected } : g));
      }
    }

    onSelect(updated);
  };

  const handleHover = (index: number, posY: number) => {
    if (activeSubDrop !== index) setSubSearch('');
    setActiveItemPosY(posY);
    setActiveSubDrop(index);
  };

  const handleDropItemKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      const rect = e.currentTarget.getBoundingClientRect();

      handleHover(index, rect.top);
    } else if (e.key === 'Escape') {
      setActiveSubDrop(undefined);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
    }
  };

  const handleSelectableDropItemKeyDown = (e: React.KeyboardEvent, subId: string, groupId?: string, groupLabel?: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (groupId && groupLabel) handleSelect(groupId, groupLabel, subId);
      else handleSelect(subId, groupLabel || subId, subId);
    } else if (e.key === 'Escape') {
      setActiveSubDrop(undefined);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
    }
  };

  const handleSearchTextKeyDown = (e: React.KeyboardEvent<HTMLParagraphElement>, value = mainFilter) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelectSearchText(value);
    }
  };

  const handleTextInputKeyPress = (key: string, value: string) => {
    if (key === 'Enter') {
      handleSelectSearchText(value);
    }
  };

  const renderTextDropdownContent = (autoFocusInput = false) => {
    const displayedTextValue = textFilterValue.trim() || selectedTextFilterValue;

    return (
      <>
        <InputArea
          data-testid={`${testId}-text-inputarea`}
          $hasError={false}
        >
          <Input
            autoComplete="off"
            autoFocus={autoFocusInput}
            change={setTextFilterValue}
            icon="search"
            id={`${displayLabel}-text-input`}
            keyPress={handleTextInputKeyPress}
            label={filterResultsText}
            maxLength={40}
            value={textFilterValue}
            size="md"
          />
        </InputArea>

        {!!displayedTextValue && (
          <SearchText
            data-testid="advanced-filter-search-text"
            onClick={() => handleSelectSearchText(displayedTextValue)}
            onKeyDown={(e) => handleSearchTextKeyDown(e, displayedTextValue)}
            role="button"
            tabIndex={0}
          >
            {filterResultsText}: {`"${displayedTextValue}"`}
          </SearchText>
        )}
      </>
    );
  };

  const getSelectedItems = (filter: FilterOrSubfilter | undefined, itemIds: string[]): SubfilterType[] => {
    if (!filter) return [];
    if ('subFilters' in filter) {
      return filter.subFilters.filter((sub) => itemIds.includes(sub.id));
    }

    return itemIds.includes(filter.id) ? [filter] : [];
  };

  const getFilteredContent = () => {
    if (!mainFilter.trim()) return null;

    const query = mainFilter.toLowerCase();
    const filtered: Record<string, { label: string; items: SubfilterType[] }> = {};

    filters.forEach((filter) => {
      if ('subFilters' in filter) {
        const enabledSubfilters = filter.subFilters.filter((sub) => !sub.isDisabled);

        const matchingSubfilters = enabledSubfilters.filter((sub) => sub.label.toLowerCase().includes(query));
        const filterLabelMatches = filter.label.toLowerCase().includes(query);

        const items = filterLabelMatches ? enabledSubfilters : matchingSubfilters;

        if (items.length > 0) {
          filtered[filter.id] = {
            label: filter.label,
            items,
          };
        }
      } else if (!filter.isDisabled && filter.label.toLowerCase().includes(query)) {
        filtered[filter.id] = { label: filter.label, items: [filter] };
      }
    });

    return filtered;
  };

  const renderCategoryContent = (category: string, selectedItems: SubfilterType[], itemIds: string[]): React.ReactNode => {
    const max = 3;

    if (selectedItems.length === 0 && itemIds.length === 1) {
      return <span>{itemIds[0]}</span>;
    }

    if (selectedItems.length > 0 && selectedItems.every((item) => item.showAsAvatar)) {
      const renderAvatar = (item: SubfilterType) => {
        const initial = ((item.avatarLabel || item.label)?.trim()?.[0] ?? '?').toUpperCase();
        const canShowImg = Boolean(item.imageUrl) && !failedImageIds[item.id];

        return (
          <div
            className="avatar"
            key={`filter-avatar-${item.id}`}
            style={!canShowImg && item.avatarColor ? { background: item.avatarColor } : undefined}
          >
            {canShowImg ? (
              <img
                src={item.imageUrl}
                alt={initial}
                onError={() => markImageFailed(item.id)}
              />
            ) : (
              <Text
                variation="body_14"
                color="brandLight"
              >
                {initial}
              </Text>
            )}
          </div>
        );
      };

      if (selectedItems.length <= max) return selectedItems.map(renderAvatar);

      return (
        <>
          {selectedItems.slice(0, 2).map(renderAvatar)}
          <span>+{selectedItems.length - 2}</span>
        </>
      );
    }

    if (selectedItems.length > 0 && selectedItems.every((item) => item.imageUrl)) {
      if (selectedItems.length <= max) {
        return selectedItems.map((item) => (
          <img
            key={`filter-image-${item.id}`}
            src={item.imageUrl}
            alt={item.label[0]}
          />
        ));
      }

      return (
        <>
          {selectedItems.slice(0, 2).map((item) => (
            <img
              key={`filter-image-${item.id}`}
              src={item.imageUrl}
              alt={item.label[0]}
            />
          ))}
          <span>+{selectedItems.length - 2}</span>
        </>
      );
    }

    if (selectedItems.length > 0 && selectedItems.every((item) => item.icon)) {
      if (selectedItems.length <= max) {
        return selectedItems.map((item) => (
          <Icon
            key={`filter-icon-${item.id}`}
            color={item.iconColor}
            name={item.icon as IconNames}
          />
        ));
      }

      return (
        <>
          {selectedItems.slice(0, 2).map((item) => (
            <Icon
              key={`filter-icon-selected-${item.id}`}
              color={item.iconColor}
              name={item.icon as IconNames}
            />
          ))}
          <span>+{selectedItems.length - 2}</span>
        </>
      );
    }

    if (selectedItems.length > 0 && selectedItems.every((item) => item.avatarColor)) {
      const renderSmallAvatar = (item: SubfilterType) => {
        const initial = ((item.avatarLabel || item.label)?.trim()?.[0] ?? '?').toUpperCase();

        return (
          <div
            className="avatar"
            key={`filter-avatar-${item.id}`}
            style={{ background: item.avatarColor }}
          >
            <Text
              variation="body_14"
              color="brandLight"
            >
              {initial}
            </Text>
          </div>
        );
      };

      if (selectedItems.length <= max) return selectedItems.map(renderSmallAvatar);

      return (
        <>
          {selectedItems.slice(0, 2).map(renderSmallAvatar)}
          <span>+{selectedItems.length - 2}</span>
        </>
      );
    }

    if (selectedItems.length === 1 && category === selectedItems[0].label) {
      return null;
    }
    if (selectedItems.length === 1) {
      return <span>{selectedItems[0].label}</span>;
    }

    return <span>{itemIds.length}</span>;
  };

  const handleFilterCardClick = (e: React.MouseEvent<HTMLDivElement>, categoryId: string) => {
    e.stopPropagation();

    if (categoryId === 'text') {
      if (openFilterCardId === categoryId) {
        setOpenFilterCardId(null);
        setActiveSubDrop(undefined);
      } else {
        setIsCollapsed(false);
        setActiveSubDrop(undefined);
        setTextFilterValue('');
        setOpenFilterCardId(categoryId);
        cardRefs.setReference(e.currentTarget);
      }

      return;
    }

    const filter = filters.find((f) => f.id === categoryId);

    if (!filter || isSubfilterType(filter)) return;

    if (openFilterCardId === categoryId) {
      setOpenFilterCardId(null);
      setActiveSubDrop(undefined);
    } else {
      setIsCollapsed(false);
      setOpenFilterCardId(categoryId);
      setSubSearch('');
      cardRefs.setReference(e.currentTarget);

      const filterIndex = filters.findIndex((f) => f.id === categoryId);

      if (filterIndex !== -1) {
        setActiveSubDrop(filterIndex);
      }
    }
  };

  const renderSubDropdownContent = (filter: FilterType, autoFocusInput = false) => (
    <>
      <InputArea
        data-testid={`${testId}-drop-inputarea`}
        $hasError={false}
      >
        <Input
          autoComplete="off"
          autoFocus={autoFocusInput}
          change={setSubSearch}
          icon="search"
          id={`${filter.label}-drop-input`}
          label={filter.label}
          maxLength={40}
          value={subSearch}
          size="md"
        />
      </InputArea>

      <ul data-testid="calendar-filters-sub-menu-list">
        {(() => {
          if (filteredSubfilters.length === 0) {
            return (
              <DropItem
                tabIndex={-1}
                style={{ pointerEvents: 'none' }}
              >
                <EmptySearchText>{`${emptyFilterText} "${subSearch}"`}</EmptySearchText>
              </DropItem>
            );
          }

          return filteredSubfilters.map((subfilter) => {
            const initial = ((subfilter.avatarLabel || subfilter.label)?.trim()?.[0] ?? '?').toUpperCase();
            const canShowImg = Boolean(subfilter.imageUrl) && !failedImageIds[subfilter.id];
            const useBackground = !canShowImg;
            const isSubfilterDisabled = subfilter.isDisabled;

            return (
              <DropItem
                data-testid={`sub-item-${subfilter.label}`}
                key={subfilter.id}
                onClick={() => !isSubfilterDisabled && handleSelect(filter.id, filter.label, subfilter.id)}
                onKeyDown={(e) => handleSelectableDropItemKeyDown(e, subfilter.id, filter.id, filter.label)}
                tabIndex={0}
                $isDisabled={isSubfilterDisabled}
              >
                <LabelGroup>
                  <Checkbox
                    id={`${subfilter.label}-checkbox`}
                    size="sm"
                    initialState={selectedFiltersByCategory[filter.id]?.includes(subfilter.id) ? 'checked' : 'unchecked'}
                    isDisabled={isSubfilterDisabled}
                    tabIndex={-1}
                  />

                  {subfilter.showAsAvatar ? (
                    <Avatar
                      $useBackground={useBackground}
                      $avatarColor={subfilter.avatarColor}
                      data-testid={`subfilter-avatar-${subfilter.id}`}
                      data-use-background={String(useBackground)}
                    >
                      {canShowImg ? (
                        <img
                          src={subfilter.imageUrl}
                          alt={initial}
                          onError={() => markImageFailed(subfilter.id)}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <Text
                          variation="body_14"
                          color="brandLight"
                        >
                          {initial}
                        </Text>
                      )}
                      {subfilter.icon && (
                        <NetworkBadge>
                          <Icon
                            color={subfilter.iconColor}
                            name={subfilter.icon}
                          />
                        </NetworkBadge>
                      )}
                    </Avatar>
                  ) : subfilter.icon ? (
                    <Icon
                      color={subfilter.iconColor}
                      name={subfilter.icon}
                    />
                  ) : subfilter.imageUrl ? (
                    <Thumbnail>
                      <img
                        src={subfilter.imageUrl}
                        alt={subfilter.label[0]}
                        loading="lazy"
                        decoding="async"
                      />
                    </Thumbnail>
                  ) : subfilter.avatarColor ? (
                    <SmallAvatar
                      $avatarColor={subfilter.avatarColor}
                      $isDisabled={isSubfilterDisabled}
                    >
                      <Text
                        variation="body_14"
                        color="brandLight"
                      >
                        {initial}
                      </Text>
                    </SmallAvatar>
                  ) : null}

                  <Label>
                    {subfilter.label}
                    {subfilter.subLabel && <CustomSpan data-testid={subfilter.label}>{subfilter.subLabel}</CustomSpan>}
                  </Label>
                  {subfilter.rightLabel && <RightLabel>{subfilter.rightLabel}</RightLabel>}
                </LabelGroup>
              </DropItem>
            );
          });
        })()}
      </ul>

      <Footer>
        <Button
          data-testid="calendar-filter-select-all-button"
          appearance="primary"
          disabled={isFooterSelectAllDisabled}
          label={allActiveSelected ? unselectedAllLabel : selectedAllLabel}
          onClick={handleSelectAllActiveGroup}
          size={12}
        />
      </Footer>
    </>
  );

  const filterCards = selectedFilters.map(({ id: categoryId, label: categoryLabel, selected: itemIds }) => {
    const filter = filters.find((f) => f.id === categoryId);
    const selectedItems = getSelectedItems(filter, itemIds);
    const content = renderCategoryContent(categoryLabel, selectedItems, itemIds);
    const isLocked = lockedFilterIds.includes(categoryId);

    return (
      <Filter
        data-testid="filter-card"
        key={categoryId}
        onClick={isLocked ? undefined : (e) => handleFilterCardClick(e, categoryId)}
        style={isLocked ? { pointerEvents: 'none', cursor: 'default' } : undefined}
      >
        <p data-testid="filter-card-text">{categoryLabel}</p>
        {content && <div>{content}</div>}
        {!isLocked && (
          <Button
            data-testid="filter-card-close-button"
            icon="close"
            onClick={(e) => {
              e.stopPropagation();

              const updated = selectedFilters.filter((g) => g.id !== categoryId);

              onSelect(updated);
              if (openFilterCardId === categoryId) {
                setOpenFilterCardId(null);
                setActiveSubDrop(undefined);
              }
            }}
            size={12}
          />
        )}
      </Filter>
    );
  });

  const renderFilteredContent = () => {
    const filteredContent = getFilteredContent();

    if (!filteredContent) return null;

    return (
      <>
        {Object.entries(filteredContent).map(([categoryId, data]) => (
          <div key={`filtered-${categoryId}`}>
            {data.items.map((item) => (
              <DropItem
                key={`filtered-${categoryId}-${item.id}`}
                onClick={() => handleSelect(categoryId, data.label, item.id)}
                tabIndex={0}
                onKeyDown={(e) => handleSelectableDropItemKeyDown(e, item.id, categoryId, data.label)}
                $isSearch
              >
                <Group>
                  {item.icon && (
                    <Icon
                      color={item.iconColor}
                      name={item.icon}
                    />
                  )}
                  {item.imageUrl && !item.icon && (
                    <Thumbnail>
                      <img
                        src={item.imageUrl}
                        alt={item.label[0]}
                      />
                    </Thumbnail>
                  )}
                  <Label>{data.label.toLowerCase() === item.label.toLowerCase() ? item.label : `${data.label} > ${item.label}`}</Label>{' '}
                </Group>
              </DropItem>
            ))}
          </div>
        ))}
        <SearchText
          data-testid="advanced-filter-search-text"
          onClick={() => handleSelectSearchText(mainFilter)}
          onKeyDown={(e) => handleSearchTextKeyDown(e, mainFilter)}
          role="button"
          tabIndex={0}
        >
          {filterResultsText}: {`"${mainFilter}"`}
        </SearchText>
      </>
    );
  };

  // Fix 5: Handle active event tracking cleanly directly via click-away logic to eliminate rendering dependency updates
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);
      const isOutsideCardDropdown = !cardDropdownRef.current || !cardDropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown && isOutsideCardDropdown) {
        setIsCollapsed(false);
        setOpenFilterCardId(null);
        setMainFilter('');
        setSubSearch('');
        setTextFilterValue('');
        setActiveSubDrop(undefined);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fix 6: Use a local variable inside the effect to track elements to satisfy strict lint rules
  useLayoutEffect(() => {
    const el = dropdownRef.current;

    if (!isCollapsed || !el) return;

    const update = () => {
      const w = el.offsetWidth;
      const left = el.offsetLeft;

      setSubDropXPos(w + left - 21);
    };

    update();

    const ro = new ResizeObserver(update);

    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isCollapsed]);

  return (
    <Container ref={containerRef}>
      <ContentArea>
        {/* STATIC AREA */}
        <DisplayArea>
          {isDisabled ? (
            <Skeleton
              height="2rem"
              width="5.3rem"
            />
          ) : (
            <>
              <Button
                data-testid="calendar-filter-button"
                appearance="default"
                customClass="filter-button"
                disabled={isDisabled}
                label={displayLabel}
                icon={icon}
                onClick={() => {
                  setMainFilter('');
                  setActiveSubDrop(undefined);
                  setIsCollapsed((prev) => {
                    const nextState = !prev;

                    if (!nextState) {
                      setSubSearch('');
                      setTextFilterValue('');
                    }

                    return nextState;
                  });
                  setOpenFilterCardId(null);
                }}
                ref={handleReferenceRef}
                size={12}
                variation="outline"
                id="advanced-filter-display-button"
              />
              <FiltersArea
                data-testid="calendar-filters-area"
                onClick={() => {
                  setIsCollapsed(false);
                  setOpenFilterCardId(null);
                  setMainFilter('');
                  setSubSearch('');
                  setTextFilterValue('');
                  setActiveSubDrop(undefined);
                }}
              >
                {filterCards}
                {selectedFilters.some((f) => !lockedFilterIds.includes(f.id)) && (
                  <Button
                    data-testid="calendar-filter-clear-button"
                    customClass="clear-button"
                    label={clearAllLabel}
                    onClick={() => {
                      onSelect(selectedFilters.filter((f) => lockedFilterIds.includes(f.id)));
                      setOpenFilterCardId(null);
                      setMainFilter('');
                      setSubSearch('');
                      setTextFilterValue('');
                      setActiveSubDrop(undefined);
                    }}
                    size={12}
                  />
                )}
              </FiltersArea>
            </>
          )}
        </DisplayArea>

        {/* DROPDOWN AREA */}
        {isCollapsed &&
          createPortal(
            <DropArea
              ref={handleFloatingRef}
              style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            >
              <InputArea
                data-testid={`${testId}-inputarea`}
                $hasError={false}
              >
                <Input
                  autoComplete="off"
                  autoFocus
                  change={setMainFilter}
                  label={inputLabel || displayLabel}
                  id={`${displayLabel}-input`}
                  icon="search"
                  keyPress={handleTextInputKeyPress}
                  maxLength={40}
                  size="md"
                  value={mainFilter}
                />
              </InputArea>
              <ul
                data-testid={`filter-${displayLabel}`}
                ref={inViewRef}
              >
                {!mainFilter.trim()
                  ? filters.map((filter, index) =>
                      isSubfilterType(filter) ? (
                        <DropItem
                          data-testid="calendar-filter-drop-item"
                          key={filter.id}
                          onClick={() => handleSelect(filter.id, filter.label, filter.id)}
                          onKeyDown={(e) => handleSelectableDropItemKeyDown(e, filter.id, filter.id, filter.label)}
                          tabIndex={0}
                        >
                          <Group>
                            <Checkbox
                              id={`${filter.label}-checkbox`}
                              size="sm"
                              initialState={selectedFiltersByCategory[filter.id]?.includes(filter.id) ? 'checked' : 'unchecked'}
                              tabIndex={-1}
                            />
                            {filter.icon && <Icon name={filter.icon} />}
                            {filter.imageUrl && !filter.icon && (
                              <Thumbnail>
                                <img
                                  src={filter.imageUrl}
                                  alt={filter.label[0]}
                                />
                              </Thumbnail>
                            )}
                            <Label>
                              {filter.label}
                              {filter.subLabel && <CustomSpan>{filter.subLabel}</CustomSpan>}
                            </Label>
                          </Group>
                        </DropItem>
                      ) : (
                        <DropItem
                          data-testid={`filter-by-${filter.label}`}
                          key={filter.id}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();

                            handleHover(index, rect.top);
                          }}
                          onKeyDown={(e) => handleDropItemKeyDown(e, index)}
                          tabIndex={0}
                        >
                          <Label data-testid={`filter-by-${filter.label}-text`}>{filter.label}</Label>
                          {!!filter.subFilters?.length && (
                            <Group>
                              {!!selectedFiltersByCategory[filter.id]?.length && (
                                <CustomSpan>{selectedFiltersByCategory[filter.id]?.length}</CustomSpan>
                              )}
                              <Icon
                                name="chevron_right"
                                color="brandDarkTint30"
                              />
                            </Group>
                          )}
                          {activeSubDrop === index && (
                            <SubdropArea
                              $isVisible
                              ref={subdropRef}
                              x={subDropXPos || 0}
                              y={subdropY}
                            >
                              <DropArea>{renderSubDropdownContent(filter as FilterType)}</DropArea>
                            </SubdropArea>
                          )}
                        </DropItem>
                      )
                    )
                  : renderFilteredContent()}
              </ul>
            </DropArea>,
            document.getElementById('dropdown-root') as HTMLElement
          )}
        {!!openFilterCardId &&
          (() => {
            if (openFilterCardId === 'text') {
              return createPortal(
                <DropArea
                  ref={handleCardFloatingRef}
                  style={{ position: cardStrategy, top: cardY ?? 0, left: cardX ?? 0 }}
                >
                  {renderTextDropdownContent(true)}
                </DropArea>,
                document.getElementById('dropdown-root') as HTMLElement
              );
            }

            const openFilter = filters.find((f) => f.id === openFilterCardId);

            if (!openFilter || isSubfilterType(openFilter)) return null;

            return createPortal(
              <DropArea
                ref={handleCardFloatingRef}
                style={{ position: cardStrategy, top: cardY ?? 0, left: cardX ?? 0 }}
              >
                {renderSubDropdownContent(openFilter, true)}
              </DropArea>,
              document.getElementById('dropdown-root') as HTMLElement
            );
          })()}
      </ContentArea>
    </Container>
  );
};
